import type { ProFormColumnsType } from '@ant-design/pro-components';
import { ResidenceRegion } from 'api/btlApplicationForm/types';
import type { BaseFormSchemaProps } from 'pages/BtlApplicationFormPage/types';
import { FormattedMessage } from 'react-intl';
import type { MessageKeys } from 'types/intl';

type OoExpensesFormSchemaProps = BaseFormSchemaProps;

function createMoneyField(
  formatMessage: OoExpensesFormSchemaProps['formatMessage'],
  labelKey: MessageKeys,
  dataIndex: string | string[],
): ProFormColumnsType {
  return {
    title: formatMessage({ id: labelKey }),
    dataIndex,
    valueType: { type: 'money', locale: 'en-GB' },
    width: 300,
    fieldProps: {
      precision: 0,
      placeholder: '£',
    },
  };
}

function createSectionHeader(formatMessage: OoExpensesFormSchemaProps['formatMessage'], labelKey: MessageKeys) {
  return {
    renderFormItem: () => <h3 className='-mb-4'>{formatMessage({ id: labelKey })}</h3>,
  };
}

export function ooExpensesFormSchema(props: OoExpensesFormSchemaProps): ProFormColumnsType[] {
  const { formatMessage } = props;

  return [
    {
      title: formatMessage({ id: 'applicationForm.DEPENDANTS.dependantsUnder13.label' }),
      dataIndex: 'numberOfDependents13OrUnder',
      valueType: 'digit',
      width: 200,
      fieldProps: {
        precision: 0,
      },
    },
    {
      title: formatMessage({ id: 'applicationForm.DEPENDANTS.dependantsOver13.label' }),
      dataIndex: 'numberOfDependentsOver13',
      valueType: 'digit',
      width: 200,
      fieldProps: {
        precision: 0,
      },
    },
    {
      title: formatMessage({ id: 'ooCase.expenses.residenceRegion.label' }),
      dataIndex: 'residenceRegion',
      valueType: 'select',
      width: 300,
      fieldProps: {
        options: Object.values(ResidenceRegion).map((type, index) => ({
          label: (
            <FormattedMessage
              id={`btlApplicationForm.TOP_SLICING_HOUSEHOLD.household.residenceRegion.${index}.label`}
            />
          ),
          value: type,
        })),
        showSearch: true,
      },
      formItemProps: {
        rules: [{ required: true }],
      },
    },
    createMoneyField(props.formatMessage, 'applicationForm.HOUSEHOLD_SPENDINGS.essentialSpending.label', [
      'declaredExpenditure',
      'basicEssentialLiving',
    ]),
    createMoneyField(props.formatMessage, 'applicationForm.HOUSEHOLD_SPENDINGS.lifestyleSpending.label', [
      'declaredExpenditure',
      'basicQualityOfLiving',
    ]),
    createSectionHeader(props.formatMessage, 'ooCase.expenses.regularOutgoings.header'),
    createMoneyField(props.formatMessage, 'applicationForm.HOUSEHOLD_OUTGOINGS.childcare.label', [
      'declaredExpenditure',
      'childcare',
    ]),
    createMoneyField(props.formatMessage, 'applicationForm.HOUSEHOLD_OUTGOINGS.maintenance.label', [
      'declaredExpenditure',
      'dependantMaintenance',
    ]),
    createMoneyField(props.formatMessage, 'applicationForm.HOUSEHOLD_OUTGOINGS.educationFees.label', [
      'declaredExpenditure',
      'educationFees',
    ]),
    createMoneyField(props.formatMessage, 'applicationForm.TRAVEL_EXPENSES.travelSpending.label', [
      'declaredExpenditure',
      'travel',
    ]),
    createSectionHeader(props.formatMessage, 'ooCase.expenses.loansAndCommitments.header'),
    createMoneyField(props.formatMessage, 'applicationForm.LOAN_REPAYMENTS.securedLoans.label', [
      'creditCommitments',
      'securedLoans',
    ]),
    createMoneyField(props.formatMessage, 'applicationForm.LOAN_REPAYMENTS.unsecuredLoans.label', [
      'creditCommitments',
      'unsecuredLoans',
    ]),
    createMoneyField(props.formatMessage, 'applicationForm.CARDS.note', ['creditCommitments', 'creditCardBalance']),
    createMoneyField(props.formatMessage, 'ooCase.expenses.studentLoans.label', [
      'creditCommitments',
      'unsecuredLoansOnRevertDate',
    ]),
  ];
}
